import {
  Box,
  Card,
  Flex,
  Icon,
  Text,
  Button,
  Heading,
  FormLabel,
  IconButton,
  FormControl,
  CurrencyInput
} from '@homebotapp/hb-react-component-catalog'
import { useState } from 'react'
import { useIntl, defineMessages } from 'react-intl'
import { formatCurrency } from '../../../../../../helpers/formatters'

export const MSG = defineMessages({
  utilities: {
    id: 'EstimatedMonthlyPaymentBreakdownUtilities.utilities',
    defaultMessage: 'Utilities'
  },
  hide: {
    id: 'EstimatedMonthlyPaymentBreakdownUtilities.hide',
    defaultMessage: 'Hide'
  },
  show: {
    id: 'EstimatedMonthlyPaymentBreakdownUtilities.show',
    defaultMessage: 'Show'
  },
  electric: {
    id: 'EstimatedMonthlyPaymentBreakdownUtilities.electric',
    defaultMessage: 'Electric'
  },
  gas: {
    id: 'EstimatedMonthlyPaymentBreakdownUtilities.gas',
    defaultMessage: 'Gas'
  },
  water: {
    id: 'EstimatedMonthlyPaymentBreakdownUtilities.water',
    defaultMessage: 'Water'
  },
  internet: {
    id: 'EstimatedMonthlyPaymentBreakdownUtilities.internet',
    defaultMessage: 'Internet'
  },
  trash: {
    id: 'EstimatedMonthlyPaymentBreakdownUtilities.trash',
    defaultMessage: 'Trash'
  },
  other: {
    id: 'EstimatedMonthlyPaymentBreakdownUtilities.other',
    defaultMessage: 'Other'
  }
})
export interface EstimatedMonthlyPaymentBreakdownUtilitiesProps {
  utilitiesGas: string
  totalUtilities: number
  utilitiesTrash: string
  utilitiesWater: string
  utilitiesOther: string
  utilitiesElectric: string
  utilitiesInternet: string
  isCalculatorsPage: boolean
  setUtilitiesGas: (utilitiesGas: string) => void
  setUtilitiesTrash: (utilitiesTrash: string) => void
  setUtilitiesWater: (utilitiesWater: string) => void
  setUtilitiesOther: (utilitiesOther: string) => void
  setUtilitiesInternet: (utilitiesInternet: string) => void
  setUtilitiesElectric: (utilitiesElectric: string) => void
  setHasUsedMortgageCalculator: (hasUsedMortgageCalculator: boolean) => void
}

export const EstimatedMonthlyPaymentBreakdownUtilities = ({
  utilitiesGas,
  totalUtilities,
  utilitiesTrash,
  utilitiesWater,
  utilitiesOther,
  setUtilitiesGas,
  utilitiesElectric,
  utilitiesInternet,
  isCalculatorsPage,
  setUtilitiesTrash,
  setUtilitiesWater,
  setUtilitiesOther,
  setUtilitiesInternet,
  setUtilitiesElectric,
  setHasUsedMortgageCalculator
}: EstimatedMonthlyPaymentBreakdownUtilitiesProps) => {
  const intl = useIntl()
  const [showEditableFields, setShowEditableFields] = useState(false)

  const handleGasChange = (val: string) => {
    setUtilitiesGas(val)
    localStorage.setItem('utilitiesGas', val)
  }

  const handleWaterChange = (val: string) => {
    setUtilitiesWater(val)
    localStorage.setItem('utilitiesWater', val)
  }

  const handleElectricChange = (val: string) => {
    setUtilitiesElectric(val)
    localStorage.setItem('utilitiesElectric', val)
  }

  const handleInternetChange = (val: string) => {
    setUtilitiesInternet(val)
    localStorage.setItem('utilitiesInternet', val)
  }

  const handleTrashChange = (val: string) => {
    setUtilitiesTrash(val)
    localStorage.setItem('utilitiesTrash', val)
  }

  const handleOtherChange = (val: string) => {
    setUtilitiesOther(val)
    localStorage.setItem('utilitiesOther', val)
  }

  return (
    <Card p={3}>
      <Button
        w='100%'
        variant='link'
        display='block'
        textAlign='left'
        fontWeight='inherit'
        textColor='currentColor'
        _hover={{ textDecoration: 'none' }}
        onClick={() => setShowEditableFields(!showEditableFields)}
      >
        <Flex align='center' justify='space-between'>
          <Flex gap={2} align='center'>
            {/* @TODO:DESIGNSYSTEM - add yellow ramp */}
            {/* @ts-ignore */}
            <Box bg='#E7C546' width={3} height={3} borderRadius='50%' />
            <Heading as='h4' size={['2xs', 'xs', 'sm']}>
              {intl.formatMessage(MSG.utilities)}
            </Heading>
          </Flex>
          <Flex>
            <Text
              mb={0}
              color='neutral.900'
              fontWeight='semibold'
              size={['xs', 'sm', 'md']}
              style={{ fontVariant: 'tabular-nums' }}
            >
              {formatCurrency(totalUtilities)}
            </Text>
            <IconButton
              size='xs'
              variant='link'
              onClick={() => setShowEditableFields(!showEditableFields)}
              aria-label={intl.formatMessage(showEditableFields ? MSG.hide : MSG.show)}
              icon={<Icon height={2} name='play' transform={`rotate(${showEditableFields ? -90 : 90}deg)`} />}
              tracking={{
                guid: 'tEMS6fHt6nEYyZqzY7kWBS',
                ui_context: isCalculatorsPage ? 'Mortgage Calculator Page' : 'Listing Details Page Mortgage Calculator',
                descriptor: `Utilities ${showEditableFields ? 'Hide' : 'Show'}`
              }}
            />
          </Flex>
        </Flex>
      </Button>
      {showEditableFields && (
        <Flex mb={2} direction='column'>
          <Flex gap={4}>
            <FormControl mt={4}>
              <FormLabel size='xs'>{intl.formatMessage(MSG.electric)}</FormLabel>
              <CurrencyInput
                size='sm'
                name='electric'
                decimalScale={2}
                defaultValue={utilitiesElectric}
                // @ts-ignore
                onChange={handleElectricChange}
                onFocus={() => setHasUsedMortgageCalculator(true)}
                tracking={{
                  descriptor: 'Electric',
                  guid: '9hFyKKGPGvKLccp4KrEAhG',
                  ui_context: isCalculatorsPage
                    ? 'Mortgage Calculator Page'
                    : 'Listing Details Page Mortgage Calculator'
                }}
              />
            </FormControl>
            <FormControl mt={4}>
              <FormLabel size='xs'>{intl.formatMessage(MSG.gas)}</FormLabel>
              <CurrencyInput
                size='sm'
                name='gas'
                decimalScale={2}
                defaultValue={utilitiesGas}
                // @ts-ignore
                onChange={handleGasChange}
                onFocus={() => setHasUsedMortgageCalculator(true)}
                tracking={{
                  descriptor: 'Gas',
                  guid: 'baWnrN1YL7rpdZp9bPbajL',
                  ui_context: isCalculatorsPage
                    ? 'Mortgage Calculator Page'
                    : 'Listing Details Page Mortgage Calculator'
                }}
              />
            </FormControl>
          </Flex>
          <Flex gap={4}>
            <FormControl mt={4}>
              <FormLabel size='xs'>{intl.formatMessage(MSG.water)}</FormLabel>
              <CurrencyInput
                size='sm'
                name='water'
                decimalScale={2}
                defaultValue={utilitiesWater}
                // @ts-ignore
                onChange={handleWaterChange}
                onFocus={() => setHasUsedMortgageCalculator(true)}
                tracking={{
                  descriptor: 'Water',
                  guid: 'anSGmk8SuKT7Q5RdPoK65y',
                  ui_context: isCalculatorsPage
                    ? 'Mortgage Calculator Page'
                    : 'Listing Details Page Mortgage Calculator'
                }}
              />
            </FormControl>
            <FormControl mt={4}>
              <FormLabel size='xs'>{intl.formatMessage(MSG.internet)}</FormLabel>
              <CurrencyInput
                size='sm'
                name='internet'
                decimalScale={2}
                defaultValue={utilitiesInternet}
                // @ts-ignore
                onChange={handleInternetChange}
                onFocus={() => setHasUsedMortgageCalculator(true)}
                tracking={{
                  descriptor: 'Internet',
                  guid: 'iHyZG1hSZsaLxTyEkBzeEo',
                  ui_context: isCalculatorsPage
                    ? 'Mortgage Calculator Page'
                    : 'Listing Details Page Mortgage Calculator'
                }}
              />
            </FormControl>
          </Flex>
          <Flex gap={4}>
            <FormControl mt={4}>
              <FormLabel size='xs'>{intl.formatMessage(MSG.trash)}</FormLabel>
              <CurrencyInput
                size='sm'
                name='trash'
                decimalScale={2}
                defaultValue={utilitiesTrash}
                // @ts-ignore
                onChange={handleTrashChange}
                onFocus={() => setHasUsedMortgageCalculator(true)}
                tracking={{
                  descriptor: 'Trash',
                  guid: '8qpbvicp2fG2GVhcGzS5bu',
                  ui_context: isCalculatorsPage
                    ? 'Mortgage Calculator Page'
                    : 'Listing Details Page Mortgage Calculator'
                }}
              />
            </FormControl>
            <FormControl mt={4}>
              <FormLabel size='xs'>{intl.formatMessage(MSG.other)}</FormLabel>
              <CurrencyInput
                size='sm'
                name='other'
                decimalScale={2}
                defaultValue={utilitiesOther}
                // @ts-ignore
                onChange={handleOtherChange}
                onFocus={() => setHasUsedMortgageCalculator(true)}
                tracking={{
                  descriptor: 'Other',
                  guid: 'mt5KhwuhgUVV8SzEnV7yuj',
                  ui_context: isCalculatorsPage
                    ? 'Mortgage Calculator Page'
                    : 'Listing Details Page Mortgage Calculator'
                }}
              />
            </FormControl>
          </Flex>
        </Flex>
      )}
    </Card>
  )
}
